import { useCallback, useEffect, useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { hasPermission } from '../../core/permissions';
import { formatBinCountdown } from '../../core/recycle-bin';
import { formatTaskDate, formatTaskWindow, taskTypeName } from '../../core/task-filters';
import { emptyRecycleBin, getDeletedTasks, restoreTask } from '../../db/task-store';
import { RECYCLE_BIN_RETENTION_DAYS, taskStatusLabel } from '../../types/task';
import type { Task, TaskType } from '../../types/task';
import type { User } from '../../types/user';

interface RecycleBinModalProps {
  user: User;
  taskTypes: readonly TaskType[];
  /** Evaluation moment for the countdown, so it never drifts mid-render. */
  now?: number;
  onClose: () => void;
  /** Re-reads the board after a restore or a purge. */
  onChanged: () => Promise<void> | void;
}

/**
 * The recycle bin (סל מחזור).
 *
 * A deleted task waits here for {@link RECYCLE_BIN_RETENTION_DAYS} days with a
 * visible countdown, and can be put back on the board at any point until it
 * runs out. "רוקן סל מחזור" takes the whole bin at once, and asks first — that
 * one is not reversible.
 */
export function RecycleBinModal({
  user,
  taskTypes,
  now = Date.now(),
  onClose,
  onChanged,
}: RecycleBinModalProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRestore = hasPermission(user, 'DELETE_TASK');

  const load = useCallback(async () => {
    setTasks(await getDeletedTasks());
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const bin = (
    <Modal
      title="סל מחזור"
      subtitle={`משימות שנמחקו נשמרות ${RECYCLE_BIN_RETENTION_DAYS} ימים ואז נמחקות לצמיתות`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            סגירה
          </button>
          {canRestore && tasks.length > 0 ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmEmpty(true)}
              className="h-10 px-4 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40 flex items-center gap-2"
            >
              <Trash2 size={16} aria-hidden />
              רוקן סל מחזור
            </button>
          ) : null}
        </>
      }
    >
      {error ? (
        <p className="mb-3 text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink/50 py-6 text-center">טוען את סל המחזור…</p>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-ink/50 py-6 text-center">סל המחזור ריק.</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id} className="rounded-xl border border-line p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{task.title}</p>
                  <p className="text-xs text-ink/50 truncate">
                    {taskTypeName(task.taskTypeId, taskTypes) || taskStatusLabel(task.statusId)} ·{' '}
                    {formatTaskDate(task.date, now)}
                    {formatTaskWindow(task) ? ` · ${formatTaskWindow(task)}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-amber-700">{formatBinCountdown(task, now)}</p>
                </div>
                {canRestore ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void run(() => restoreTask(user, task.id))}
                    className="shrink-0 h-9 px-3 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium hover:bg-brand-100 disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <RotateCcw size={15} aria-hidden />
                    שחזר משימה
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );

  return (
    <>
      {bin}
      {confirmEmpty ? (
        <Modal
          title="ריקון סל המחזור"
          onClose={() => setConfirmEmpty(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setConfirmEmpty(false)}
                className="h-10 px-4 rounded-xl border border-line text-sm"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await emptyRecycleBin(user);
                    setConfirmEmpty(false);
                  })
                }
                className="h-10 px-4 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                מחק לצמיתות
              </button>
            </>
          }
        >
          <p className="text-sm">
            {tasks.length} משימות יימחקו לצמיתות יחד עם הצוותים, הדיווחים וציר הזמן שלהן. לא ניתן
            לשחזר אותן ללא קובץ גיבוי.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
